import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  GetJobsiteConfigurationByProjectDocument,
  SaveJobsiteConfigurationDocument,
} from '~/client/graph/operations/generated/JobsiteHomepageConfiguration.generated'
import defaultWidgetConfig from '~/client/static/default-widget-config.json'

import {
  LOAD_JOBSITE_HOMEPAGE_CONFIGURATION,
  SAVE_JOBSITE_HOMEPAGE_CONFIGURATION,
} from '../EventStore/eventConstants'
import InitialState from '../InitialState'
import GraphExecutorStore from './GraphExecutor.store'

// Type definition matching backend JobsiteConfiguration
interface IJobsiteConfiguration {
  id?: string
  projectId: string
  configuration?: string // JSON string of { widgets: WidgetConfig[], settings?: BoardSettings }
  createdAt?: Date
  updatedAt?: Date
  version?: number
}

// Board-level settings from JHP (header image offset, etc.)
interface BoardSettings {
  headerImageOffsetY?: number
  selectedEntryGateIds?: string[]
  accessPoints?: { selectedIds: string[]; title: string }
}

/**
 * Store for managing Jobsite Homepage widget configuration
 * Handles loading, saving, and providing fallback configuration
 */
export default class JobsiteHomepageConfigurationStore {
  @observable public configuration: IJobsiteConfiguration | null = null
  @observable public isLoaded: boolean = false
  @observable public defaultConfiguration: any = null
  // Owned here, not by the container: the mobile route slider remounts it on every navigation.
  // Three pieces of state, because "which project do we hold" and "which project are we asking
  // about" are different questions and conflating them let one project's response land on another.
  private loadedProjectId: string | null = null
  private pendingProjectId: string | null = null
  private pendingLoad: Promise<void> | null = null
  private loadRequestId: number = 0

  public constructor(
    private readonly state: InitialState,
    private readonly graphExecutorStore: GraphExecutorStore,
  ) {
    this.resetToDefault()
    makeObservable(this)
    this.loadDefaultConfiguration()
  }

  /**
   * Load default configuration from static file as fallback
   * This is used when no saved configuration exists in the database
   */
  @action.bound
  private loadDefaultConfiguration() {
    try {
      this.defaultConfiguration = defaultWidgetConfig
    } catch (error) {
      console.error('Failed to load default JHP configuration:', error)
      // Provide a minimal fallback if static file can't be loaded
      this.defaultConfiguration = { widgets: [] }
    }
  }

  @action.bound
  public resetToDefault() {
    this.configuration = {
      configuration: JSON.stringify({ widgets: [] }),
      projectId: this.state.activeProject?.id || '',
    }
    this.isLoaded = false
    this.loadedProjectId = null
  }

  /**
   * Load configuration from backend
   * Falls back to default configuration if none exists
   */
  @action.bound
  public async load(): Promise<void> {
    const projectId = this.state.activeProject?.id
    if (!projectId) {
      console.warn('Cannot load JHP configuration: no active project')
      return
    }

    // Already asking about this project: share that request rather than racing a duplicate,
    // where whichever response lands second wins regardless of which succeeded.
    if (this.pendingProjectId === projectId && this.pendingLoad) {
      return this.pendingLoad
    }

    if (this.loadedProjectId === projectId) {
      // Back on a project we already hold. Any request still running is for a different project
      // and must be abandoned, or it would commit its configuration over this one.
      if (this.pendingProjectId !== null) {
        this.abandonPendingLoad()
      }
      return
    }

    this.pendingLoad = this.runLoad(projectId)

    return this.pendingLoad
  }

  @action
  private abandonPendingLoad(): void {
    // Bumping the id is what makes the in-flight response stale; nothing can cancel the query.
    this.loadRequestId++
    this.pendingProjectId = null
    this.pendingLoad = null
    this.isLoaded = true
    this.state.loading.set(LOAD_JOBSITE_HOMEPAGE_CONFIGURATION, false)
  }

  private async runLoad(projectId: string): Promise<void> {
    // Consumers gate on isLoaded, but the previous configuration stays put, so nothing renders
    // an empty board while this is in flight.
    this.isLoaded = false
    this.pendingProjectId = projectId
    const requestId = ++this.loadRequestId
    const isCurrent = () => requestId === this.loadRequestId

    this.state.loading.set(LOAD_JOBSITE_HOMEPAGE_CONFIGURATION, true)

    try {
      const result = await this.graphExecutorStore.query(
        GetJobsiteConfigurationByProjectDocument,
        {
          projectId,
        },
      )

      runInAction(() => {
        if (!isCurrent()) return

        const loaded = result.data?.jobsiteConfiguration

        this.configuration = loaded || {
          // No saved configuration, use default
          projectId,
          configuration: JSON.stringify(this.defaultConfiguration),
        }
        // Only a real configuration marks the project done. An empty result is indistinguishable
        // from a query that resolved before a project switch settled, and latching it would pin
        // the default board with nothing left to ask again.
        this.loadedProjectId = loaded ? projectId : null
        this.isLoaded = true
      })
    } catch (error) {
      console.error('Failed to load JHP configuration:', error)
      runInAction(() => {
        if (!isCurrent()) return

        this.loadedProjectId = null
        this.configuration = {
          projectId,
          configuration: JSON.stringify(this.defaultConfiguration),
        }
        this.isLoaded = true
      })
    } finally {
      runInAction(() => {
        if (!isCurrent()) return

        this.pendingProjectId = null
        this.pendingLoad = null
        this.state.loading.set(LOAD_JOBSITE_HOMEPAGE_CONFIGURATION, false)
      })
    }
  }

  /**
   * Save widget configuration to backend
   * @param widgetConfig - Array of WidgetConfig objects from JHP
   * @param settings - Optional board-level settings (header image offset, etc.)
   */
  @action.bound
  public async save(widgetConfig: any[], settings?: BoardSettings) {
    // Captured once: a switch mid-save would otherwise send the mutation to a different project
    // than the one the optimistic write was built for.
    const projectId = this.state.activeProject?.id
    if (!projectId) {
      console.warn('Cannot save JHP configuration: no active project')
      return
    }

    this.state.loading.set(SAVE_JOBSITE_HOMEPAGE_CONFIGURATION, true)

    // Serialize widget config and settings to JSON string
    const configuration = JSON.stringify({ widgets: widgetConfig, settings })
    const previousConfiguration = this.configuration
    // By content, not identity: `configuration` is a deep observable, so the optimistic write
    // below stores a proxy and an identity check could never match.
    const isStillOurWrite = () =>
      this.configuration?.projectId === projectId &&
      this.configuration?.configuration === configuration

    try {
      // Update local configuration optimistically
      runInAction(() => {
        this.configuration = {
          ...this.configuration,
          projectId,
          configuration,
        }
      })

      // Save to backend
      await this.graphExecutorStore.mutate(SaveJobsiteConfigurationDocument, {
        input: {
          projectId,
          configuration,
        },
      })

      console.log('JHP Configuration saved successfully')

      runInAction(() => {
        // Only for the project still on screen: a switch during the mutation means a load for
        // the new project owns the store now, and claiming this one would open its gate on the
        // old board.
        if (isStillOurWrite() && this.state.activeProject?.id === projectId) {
          // Only a read of this same project is stale by definition.
          if (this.pendingProjectId === projectId) {
            this.abandonPendingLoad()
          }
          this.loadedProjectId = projectId
        }

        this.state.loading.set(SAVE_JOBSITE_HOMEPAGE_CONFIGURATION, false)
      })
    } catch (error) {
      console.error('Failed to save JHP configuration:', error)
      runInAction(() => {
        // Nothing was persisted, so the optimistic board must not survive as the cached truth.
        if (isStillOurWrite()) {
          this.configuration = previousConfiguration
          if (this.loadedProjectId === projectId) {
            this.loadedProjectId = null
          }
        }

        this.state.loading.set(SAVE_JOBSITE_HOMEPAGE_CONFIGURATION, false)
      })
      throw error
    }
  }

  // A derivation, not a method: readers subscribe to `configuration` and stop getting a fresh
  // identity from a re-parse on every read.
  @computed
  public get parsedConfiguration(): any {
    if (!this.configuration?.configuration) {
      return this.defaultConfiguration || { widgets: [] }
    }

    try {
      return JSON.parse(this.configuration.configuration)
    } catch (error) {
      console.error('Failed to parse JHP configuration:', error)
      return this.defaultConfiguration || { widgets: [] }
    }
  }

  /**
   * Update configuration from backend subscription or query result
   */
  @action.bound
  public updateConfiguration(config: IJobsiteConfiguration | null) {
    if (config) {
      this.configuration = config
    } else {
      this.resetToDefault()
    }
    this.isLoaded = true
  }
}
